import { isPlatformBrowser } from '@angular/common';
import { Component, computed, DOCUMENT, effect, inject, InjectionToken, PLATFORM_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';

import { BreadcrumbStructuredDataItem, SeoUtils } from '../tools/seo-utils';
import {
  BreadcrumbItem,
  breadcrumbParentForRoute,
  DEFAULT_LANG,
  Lang,
  LANG_PREFIXES,
  langFromUrl,
  LANGUAGES,
  matchUrlToRoute,
  resolveRoutePath,
  titleKeyForRoute,
} from './app.routes-config';
import { Breadcrumb } from './components/common/breadcrumb/breadcrumb';
import { Footer } from './components/common/footer/footer';
import { SearchOverlay } from './components/common/search-overlay/search-overlay';
import { SiteHeader } from './components/common/site-header/site-header';
import { AnalyticsService } from './services/analytics.service';
import { BreadcrumbService } from './services/breadcrumb.service';
import { DocumentTitleService } from './services/document-title.service';
import { InternalTrafficService } from './services/internal-traffic.service';
import { UserSessionService } from './services/user-session.service';
import Config from './utils/config';

export interface PageViewTitleTiming {
  maxWaitMs: number;
  settleMs: number;
}

export const PAGE_VIEW_TITLE_TIMING = new InjectionToken<PageViewTitleTiming>('PAGE_VIEW_TITLE_TIMING', {
  factory: (): PageViewTitleTiming => ({ maxWaitMs: 4000, settleMs: 600 }),
});

@Component({
  imports: [RouterOutlet, SiteHeader, Footer, SearchOverlay, Breadcrumb],
  selector: 'app-root',
  standalone: true,
  styleUrl: './app.scss',
  templateUrl: './app.html',
})
export class App {
  private baseBreadcrumbTrail = signal<BreadcrumbItem[]>([]);

  private breadcrumbService = inject(BreadcrumbService);

  protected readonly breadcrumbTrail = computed<BreadcrumbItem[]>(() => {
    const trail = this.baseBreadcrumbTrail();
    const label = this.breadcrumbService.currentLabel();
    if (!label || trail.length === 0) return trail;

    return [...trail.slice(0, -1), { ...trail[trail.length - 1], label }];
  });

  private router = inject(Router);

  protected readonly isAdminArea = signal(this.isAdminUrl(this.router.url));

  protected readonly pageVisible = signal(true);

  private analytics = inject(AnalyticsService);

  private breadcrumbLang = signal<Lang>(DEFAULT_LANG);

  private translate = inject(TranslateService);

  private readonly breadcrumbStructuredDataItems = computed<BreadcrumbStructuredDataItem[]>(() => {
    const trail = this.breadcrumbTrail();
    const lang = this.breadcrumbLang();
    const currentUrl = this.breadcrumbService.currentUrl();
    const items = trail.map((item, index) => ({
      name: item.label ?? this.translate.instant(item.labelKey),
      url: index === trail.length - 1 && currentUrl ? currentUrl : this.breadcrumbItemUrl(item, lang),
    }));

    return this.breadcrumbService.currentPageMissing() ? items.slice(0, -1) : items;
  });

  private document = inject(DOCUMENT);

  private documentTitle = inject(DocumentTitleService);

  private hasNavigatedOnce = false;

  private hasTrackedPageView = false;

  private internalTraffic = inject(InternalTrafficService);

  private pageViewTitleTiming = inject(PAGE_VIEW_TITLE_TIMING);

  private pendingPageViewMaxTimer: ReturnType<typeof setTimeout> | null = null;

  private pendingPageViewPreviousTitle: string | null = null;

  private pendingPageViewSettleTimer: ReturnType<typeof setTimeout> | null = null;

  private pendingPageViewUrl: string | null = null;

  private platformId = inject(PLATFORM_ID);

  private userSession = inject(UserSessionService);

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.isAdminArea.set(this.isAdminUrl(this.router.url));
        this.updateBreadcrumbs();
        this.trackPageView(this.router.url);
        this.animatePageTransition();
      });

    effect(() => {
      this.documentTitle.title();
      if (this.pendingPageViewUrl === null) return;

      this.restartPageViewSettleTimer();
    });

    effect(() => {
      if (!this.userSession.session()?.isAdmin) return;

      this.internalTraffic.markInternal();
      this.analytics.markInternalTraffic();
    });

    effect(() => {
      SeoUtils.setStructuredData(this.document, this.breadcrumbStructuredDataItems(), this.breadcrumbLang());
    });

    this.updateBreadcrumbs();
  }

  private animatePageTransition(): void {
    if (!this.hasNavigatedOnce) {
      this.hasNavigatedOnce = true;
      return;
    }

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.pageVisible.set(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.pageVisible.set(true));
    });
  }

  private breadcrumbItemUrl(item: BreadcrumbItem, lang: Lang): string {
    if (item.key === 'home') {
      const langPrefix = LANG_PREFIXES[lang];

      return `${Config.BASE_URL}${langPrefix ? `/${langPrefix}` : ''}`;
    }

    return `${Config.BASE_URL}${resolveRoutePath(item.key, lang, item.params)}`;
  }

  private clearPendingPageView(): void {
    this.pendingPageViewPreviousTitle = null;
    this.pendingPageViewUrl = null;

    if (this.pendingPageViewMaxTimer !== null) {
      clearTimeout(this.pendingPageViewMaxTimer);
      this.pendingPageViewMaxTimer = null;
    }

    if (this.pendingPageViewSettleTimer !== null) {
      clearTimeout(this.pendingPageViewSettleTimer);
      this.pendingPageViewSettleTimer = null;
    }
  }

  private flushPendingPageView(): void {
    const url = this.pendingPageViewUrl;
    const previousTitle = this.pendingPageViewPreviousTitle;
    this.clearPendingPageView();

    if (url === null) return;

    const title = this.document.title;
    this.sendPageView(url, title === previousTitle ? this.resolvePageTitle(url) : title);
  }

  private isAdminUrl(url: string): boolean {
    const path = url.split(/[?#]/)[0];

    return LANGUAGES.some((lang) => {
      const prefix = LANG_PREFIXES[lang];
      const adminPath = prefix ? `/${prefix}/admin` : '/admin';

      return path === adminPath || path.startsWith(`${adminPath}/`);
    });
  }

  private resolvePageTitle(url: string): string {
    const match = matchUrlToRoute(url);
    if (!match) return this.document.title;

    return this.translate.instant(titleKeyForRoute(match.key));
  }

  private restartPageViewSettleTimer(): void {
    if (this.pendingPageViewSettleTimer !== null) clearTimeout(this.pendingPageViewSettleTimer);

    this.pendingPageViewSettleTimer = setTimeout(() => this.flushPendingPageView(), this.pageViewTitleTiming.settleMs);
  }

  private sendPageView(url: string, title: string): void {
    this.clearPendingPageView();
    this.analytics.trackPageView(url, title);
  }

  private trackPageView(url: string): void {
    this.flushPendingPageView();

    const isFirstPageView = !this.hasTrackedPageView;
    this.hasTrackedPageView = true;

    if (this.isAdminUrl(url)) return;

    if (isFirstPageView) {
      this.sendPageView(url, this.document.title);
      return;
    }

    this.pendingPageViewPreviousTitle = this.document.title;
    this.pendingPageViewUrl = url;
    this.pendingPageViewMaxTimer = setTimeout(() => this.flushPendingPageView(), this.pageViewTitleTiming.maxWaitMs);
  }

  private updateBreadcrumbs(): void {
    const url = this.router.url;
    const match = matchUrlToRoute(url);
    const lang = match?.lang ?? langFromUrl(url);
    const trail: BreadcrumbItem[] = [{ key: 'home', labelKey: 'nav.home', params: {} }];

    if (match && match.key !== 'home') {
      const parentKey = breadcrumbParentForRoute(match.key);
      if (parentKey) trail.push({ key: parentKey, labelKey: titleKeyForRoute(parentKey), params: {} });

      trail.push({ key: match.key, labelKey: titleKeyForRoute(match.key), params: match.params });
    }

    this.breadcrumbLang.set(lang);
    this.baseBreadcrumbTrail.set(trail);
    this.breadcrumbService.reset();
  }
}
